<template>
  <div
    class="capsule-outer"
    :class="{
      animated: props.mode === 'animated',
      main: props.isMain,
      horizontal: props.mode === 'horizontal',
      dark: props.background === 'dark',
      pharmaprograms: props.pharmaProgrmas,
      glow: props.glow
    }"
  >
    <div v-if="!props.isMain && props.mode !== 'horizontal'" class="blank"></div>
    <div
      v-for="index in props.childrenCount"
      :key="index"
      class="child"
      :class="{pdf: !isMain}"
      :style="childStyles(index - 1)"
      @click="props.mode === 'animated' ? handleExpand(index - 1) : null"
    >
      <slot :name="index" :index="index - 1" />
    </div>
    <div v-if="props.pharmaProgrmas" class="blank"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

interface IProps {
  mode: 'static' | 'animated' | 'horizontal'
  childrenCount: number
  isMain: boolean
  background: 'dark' | 'light'
  pharmaProgrmas?: boolean
  glow?: boolean
  pdf?: boolean
  expandedIndex?: number
}

const emit = defineEmits(['update:expandedIndex'])

const props = defineProps<IProps>()

// Track the currently expanded child (default to first child in animated mode)
const expandedIndex = ref(props.expandedIndex ?? 0)

// Compute styles for children based on the mode, expanded state, and rounded corners
const childStyles = computed(() => {
  return (index: number) => {
    const isFirstChild = index === 0
    const isLastChild = index === props.childrenCount - 1
    const isSingleChild = props.childrenCount === 1

    const flexValue =
      props.mode === 'static'
        ? isFirstChild
          ? props.isMain
            ? '3'
            : '2.75'
          : '1'
        : props.mode === 'animated'
        ? index === expandedIndex.value
          ? props.isMain
            ? '2'
            : '1.75'
          : '1'
        : props.mode === 'horizontal'
        ? isFirstChild || isLastChild
          ? '1.1'
          : '1'
        : '1'

    const borderRadius = isSingleChild
      ? '2500px'
      : props.mode === 'horizontal'
      ? isFirstChild
        ? '2500px 0 0 2500px'
        : isLastChild
        ? '0 2500px 2500px 0'
        : '0'
      : isFirstChild
      ? '2500px 2500px 0 0'
      : isLastChild
      ? '0 0 2500px 2500px'
      : '0'

    return {
      flex: flexValue,
      borderRadius,
    }
  }
})


// Handle click on a child to expand it
const handleExpand = (index: number) => {
  expandedIndex.value = index
  emit('update:expandedIndex', expandedIndex.value)
}
</script>

<style scoped>
.capsule-outer {
  display: flex;
  gap: 1.5%;
  flex: 0.9;
  min-height: 52.5rem;
  max-height: 90vh;
  flex-direction: column;
  max-width: clamp(17.1875rem, 15vw, 46.875rem);
  min-width: 15rem;
  position: relative;
}

.capsule-outer.horizontal {
  flex: 1;
  flex-direction: row;
  min-height: unset;
  max-width: unset;
  gap: 0.625rem;
  position: relative;
}

.capsule-outer.main {
  flex: 1.15;
  position: relative;
  max-width: clamp(21.875rem, 20vw, 62.5rem);
}

.blank {
  height: 3dvh;
}

.child {
  background-color: var(--color-background-bright);
  transition:
    background-color 0.5s ease,
    flex 0.5s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
  overflow: hidden;
}

.capsule-outer.animated .child {
  cursor: pointer;
}

.capsule-outer.dark .child {
  background-color: var(--color-secondary);
  color: var(--color-background-bright);
}

/* If the back panel is main panel show glow */
.capsule-outer.main.glow::before {
  content: '';
  width: 70%;
  aspect-ratio: 1.25;
  background-color: var(--color-arrotex-3);
  position: absolute;
  top: -1%;
  right: -2%;
  z-index: -1;
  border-radius: 50%;
  filter: blur(2.5rem);
  opacity: 0.75;
}

.capsule-outer.pharmaprograms.glow::before {
  content: '';
  position: absolute;
  aspect-ratio: 1;
  background-color: var(--color-axe-3);
  width: 100%;
  top: 1.5%;
  right: 0%;
  border-radius: 50%;
  filter: blur(2.5rem);
  opacity: 0.75;
  z-index: -1;
}

.capsule-outer.main.glow:after {
  content: '';
  width: 60%;
  aspect-ratio: 1;
  background-color: var(--color-vidacorp-3);
  position: absolute;
  bottom: -3%;
  left: -1%;
  z-index: -1;
  border-radius: 50%;
  filter: blur(2.5rem);
  opacity: 0.75;
}

.capsule-outer.pharmaprograms.glow::after {
  background-color: transparent;
}

/* If mode is horizontal show glow */
.capsule-outer.first::before {
  content: '';
  width: 30%;
  aspect-ratio: 1.25;
  background-color: var(--color-arrotex-3);
  position: absolute;
  top: -1%;
  left: -2%;
  z-index: -1;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.75;
}

.capsule-outer.last::after {
  content: '';
  width: 30%;
  aspect-ratio: 1.25;
  background-color: var(--color-arrotex-3);
  position: absolute;
  bottom: -1%;
  right: -2%;
  z-index: -1;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.75;
}

</style>
